import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/credit.png';

import { Database, Zap, Globe, Clock, TrendingDown, Activity } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/credit-ratings');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/credit-migration-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/credit-covenant-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/institutional-health-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/ratings-pressure-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const CreditPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Credit Ratings Data API – Agency Ratings & Risk Data"
        description="Credit ratings data API with agency ratings from S&P, Moody's, and Fitch. Credit risk data and CDS spreads for 50,000+ issuers, back to 1987."
        canonicalUrl="https://axionquant.com/products/credit"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Credit Ratings Data API",
          "description": "Credit ratings data API with agency ratings from S&P, Moody's, and Fitch. Credit risk data and CDS spreads for 50,000+ issuers, back to 1987.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Credit Ratings Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

        <Header
    docs="/docs/04-macros-and-signals/credit-ratings"
   title="Credit Ratings Data API – Agency Ratings & Risk Data"
  body="Access daily agency credit ratings, bond ratings data, and credit risk metrics through a single credit rating API - normalized and ready for portfolio screening, watchlist monitoring, and cross-asset risk analysis."
  crumb={{name: 'Credit', path:'/products/credit'}}
  tabs={[
    {
      label: 'Ratings Screener',
      language: 'python',
      filename: 'ratings_screener.py',
      bg: '13.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

issuers = ["Apple", "Microsoft", "Amazon", "Tesla", "ExxonMobil"]

def fetch_ratings(row):
    name    = row["name"]
    results = client.credit.search(name)
    ratings = client.credit.ratings(results[0]["id"]) if results else []
    return (name, ratings)

df_issuers  = utils.df([{"name": n} for n in issuers])
ratings_map = {}
utils.work(df_issuers, fetch_ratings, ratings_map)

flat = [
    {
        "issuer":  issuer,
        "agency":  r["agency"],
        "rating":  r["rating"],
        "outlook": r.get("outlook", "N/A"),
    }
    for issuer, ratings in ratings_map.items()
    for r in ratings
]

df_ratings = utils.df(flat)
visualize.bar(df_ratings, x="issuer", y="rating")
`,
    },
    {
      label: 'Agency Divergence',
      language: 'python',
      filename: 'agency_divergence.py',
      bg: '5.jpeg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

entity   = client.credit.search("Tesla")
ratings  = client.credit.ratings(entity[0]["id"])

df_ratings = utils.df(ratings)

sp      = next((r for r in ratings if r["agency"] == "S&P"),      None)
moodys  = next((r for r in ratings if r["agency"] == "Moody's"),  None)
fitch   = next((r for r in ratings if r["agency"] == "Fitch"),    None)

divergence = {
    "S&P":      sp["rating"]     if sp     else "N/A",
    "Moody's":  moodys["rating"] if moodys else "N/A",
    "Fitch":    fitch["rating"]  if fitch  else "N/A",
}

df_div = utils.df([
    {"agency": k, "rating": v}
    for k, v in divergence.items()
])

visualize.bar(df_div, x="agency", y="rating")
`,
    },
    {
      label: 'Credit vs. Financials',
      language: 'python',
      filename: 'credit_vs_financials.py',
      bg: '11.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

universe = [
    {"name": "Apple",     "ticker": "AAPL"},
    {"name": "Amazon",    "ticker": "AMZN"},
    {"name": "Microsoft", "ticker": "MSFT"},
    {"name": "Tesla",     "ticker": "TSLA"},
]

combined = []

for row in universe:
    entity  = client.credit.search(row["name"])
    ratings = client.credit.ratings(entity[0]["id"]) if entity else []
    sp      = next((r["rating"] for r in ratings if r["agency"] == "S&P"), "N/A")
    metrics = client.financials.metrics(row["ticker"])

    combined.append({
        "ticker":       row["ticker"],
        "sp_rating":    sp,
        "debt_equity":  metrics["debtToEquity"],
        "gross_margin": metrics["grossMargin"],
        "roe":          metrics["returnOnEquity"],
    })

df = utils.df(combined)

visualize.scatter(
    df,
    x="debt_equity",
    y="gross_margin",
    hover=["ticker", "sp_rating"],
)
`,
    },
    {
      label: 'Ratings Watchlist',
      language: 'python',
      filename: 'ratings_watchlist.py',
      bg: '14.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

watchlist = ["Boeing", "Ford", "AT&T", "Pfizer", "ExxonMobil"]

def fetch(row):
    name    = row["name"]
    results = client.credit.search(name)
    ratings = client.credit.ratings(results[0]["id"]) if results else []
    outlook = next(
        (r.get("outlook", "N/A") for r in ratings if r["agency"] == "S&P"),
        "N/A"
    )
    return (name, {"name": name, "outlook": outlook, "agencies": len(ratings)})

df_watch  = utils.df([{"name": n} for n in watchlist])
watch_map = {}
utils.work(df_watch, fetch, watch_map)

df_result = utils.df(list(watch_map.values()))

negative  = utils.filter(df_result, col="outlook", items=["Negative", "CreditWatch Negative"])

visualize.bar(df_result, x="name", y="agencies")
`,
    },
  ]}
/>
      <Marquee/>

      <FeaturesGrid
        title="Credit Ratings Data API. "
        subtitle="Agency ratings, bond ratings data, and credit risk data. "
        features={ [
        {
          fig: 'FIG 0.1',
          icon: <Zap size={80} color="var(--purple)" />,
          title: 'Daily & Historical Ratings',
          description: 'Daily agency ratings, outlooks, and watch status from S&P, Moody\'s, Fitch, and DBRS. Historical migrations mapped to exact timestamps for backtesting.',
        },
        {
          fig: 'FIG 0.2',
          icon: <Activity size={80} color="var(--green)" />,
          title: 'CDS & Spread Intelligence',
          description: 'Monitor CDS curves across all tenors, track bond yield spreads, and analyze credit risk data and liquidity metrics for corporate and sovereign issuers.',
        },
        {
          fig: 'FIG 0.3',
          icon: <Database size={80} color="var(--red)" />,
          title: 'Bond Pricing & Analytics',
          description: 'Daily prices, yields, duration, convexity, and OAS for investment-grade and high-yield bonds, with ratings data across 2 million+ securities and deep history for backtesting.',
        },
        {
          fig: 'FIG 0.4',
          icon: <TrendingDown size={80} color="var(--purple)" />,
          title: 'Default & Recovery Data',
          description: 'Historical default rates, recovery estimates, and market-implied probability of default models. Integrate directly into your risk systems.',
        },
        {
          fig: 'FIG 0.5',
          icon: <Globe size={80} color="var(--red)" />,
          title: 'Global Coverage',
          description: 'Corporate, sovereign, and municipal bonds from 85+ markets. Cross-border normalization so you can compare issuers across regions instantly.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Clock size={80} color="var(--green)" />,
          title: '35+ Years of History',
          description: 'Deep historical credit data for backtesting and modeling. Rating transitions, spread history, and default cycles across multiple economic regimes.',
        },
      ]}/>

      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Credit Intelligence Platform"
        headingPlain="Credit ratings data"
        headingEm="for 50,000+ issuers."
        body="Institutional-grade credit ratings data feeds spanning agency ratings, CDS, bonds, and defaults - all normalized, timestamped, and ready for production."
        stats={[
          { v: "50K+",  l: "Issuers"        },
          { v: "2M+",   l: "Bonds"          },
          { v: "4+",    l: "Rating Agencies" },
          { v: "35yr",  l: "History"        },
        ]}
        markers={[
          { id: "ny",          location: [40.71, -74.01],  label: "New York" },
          { id: "london",      location: [51.51, -0.09],   label: "London" },
          { id: "tokyo",       location: [35.68, 139.77],  label: "Tokyo" },
          { id: "hk",          location: [22.28, 114.16],  label: "Hong Kong" },
          { id: "singapore",   location: [1.35, 103.82],   label: "Singapore" },

          { id: "paris",       location: [48.86, 2.35],    label: "Paris" },
          { id: "frankfurt",   location: [50.11, 8.68],    label: "Frankfurt" },
          { id: "zurich",      location: [47.38, 8.54],    label: "Zurich" },
          { id: "amsterdam",   location: [52.37, 4.90],    label: "Amsterdam" },
          { id: "madrid",      location: [40.42, -3.70],   label: "Madrid" },
          { id: "milan",       location: [45.46, 9.19],    label: "Milan" },
          { id: "brussels",    location: [50.85, 4.35],    label: "Brussels" },
          { id: "stockholm",   location: [59.33, 18.07],   label: "Stockholm" },
          { id: "oslo",        location: [59.91, 10.75],   label: "Oslo" },
          { id: "copenhagen",  location: [55.68, 12.57],   label: "Copenhagen" },

          { id: "toronto",     location: [43.65, -79.38],  label: "Toronto" },
          { id: "vancouver",   location: [49.28, -123.12], label: "Vancouver" },
          { id: "chicago",     location: [41.88, -87.63],  label: "Chicago" },
          { id: "boston",      location: [42.36, -71.06],  label: "Boston" },
          { id: "washington",  location: [38.90, -77.04],  label: "Washington DC" },
          { id: "sanfrancisco",location: [37.77, -122.42], label: "San Francisco" },
          { id: "losangeles",  location: [34.05, -118.24], label: "Los Angeles" },
          { id: "houston",     location: [29.76, -95.37],  label: "Houston" },

          { id: "seoul",       location: [37.57, 126.98],  label: "Seoul" },
          { id: "beijing",     location: [39.90, 116.40],  label: "Beijing" },
          { id: "shanghai",    location: [31.23, 121.47],  label: "Shanghai" },
          { id: "taipei",      location: [25.03, 121.57],  label: "Taipei" },
          { id: "mumbai",      location: [19.07, 72.87],   label: "Mumbai" },
          { id: "delhi",       location: [28.61, 77.20],   label: "Delhi" },
          { id: "bangkok",     location: [13.75, 100.50],  label: "Bangkok" },
          { id: "jakarta",     location: [-6.20, 106.85],  label: "Jakarta" },

          { id: "sydney",      location: [-33.87, 151.21], label: "Sydney" },
          { id: "melbourne",   location: [-37.81, 144.96], label: "Melbourne" },
          { id: "auckland",    location: [-36.85, 174.76], label: "Auckland" },

          { id: "dubai",       location: [25.20, 55.27],   label: "Dubai" },
          { id: "abu_dhabi",   location: [24.45, 54.38],   label: "Abu Dhabi" },
          { id: "riyadh",      location: [24.71, 46.67],   label: "Riyadh" },
          { id: "telaviv",     location: [32.08, 34.78],   label: "Tel Aviv" },
          { id: "istanbul",    location: [41.01, 28.97],   label: "Istanbul" },

          { id: "sao_paulo",   location: [-23.55, -46.63], label: "São Paulo" },
          { id: "mexico_city", location: [19.43, -99.13],  label: "Mexico City" },
          { id: "buenos_aires",location: [-34.60, -58.38], label: "Buenos Aires" },
          { id: "santiago",    location: [-33.45, -70.66], label: "Santiago" },

          { id: "johannesburg",location: [-26.20, 28.04],  label: "Johannesburg" },
          { id: "lagos",       location: [6.52, 3.38],     label: "Lagos" },
          { id: "cairo",       location: [30.04, 31.24],   label: "Cairo" },
          { id: "nairobi",     location: [-1.29, 36.82],   label: "Nairobi" },

          { id: "warsaw",      location: [52.23, 21.01],   label: "Warsaw" },
          { id: "prague",      location: [50.08, 14.43],   label: "Prague" },
          { id: "vienna",      location: [48.21, 16.37],   label: "Vienna" },
          { id: "athens",      location: [37.98, 23.72],   label: "Athens" },
          { id: "lisbon",      location: [38.72, -9.14],   label: "Lisbon" },

          { id: "moscow",      location: [55.75, 37.62],   label: "Moscow" },
          { id: "helsinki",    location: [60.17, 24.94],   label: "Helsinki" },
          { id: "reykjavik",   location: [64.15, -21.94],  label: "Reykjavik" }
        ]}
      />

        <DataGrid
          title="Credit Ratings Data API Without Blind Spots"
          subtitle="Comprehensive credit market data across corporate and sovereign bonds. Bond ratings data, CDS spreads, yield curves, and default risk metrics from all major agencies and global markets, normalized for serious analysis."
          bg={databg}
          stats={[
            { label: 'Issuers', value: '50,000+' },
            { label: 'CDS Curves & Tenors', value: '500,000+' },
            { label: 'Markets Covered', value: '85+' },
          ]}

        />
        <CardList
          title="Decode Credit Risk with Precision"
          vid={'https://cdn.studio14a.com/axion/products/cards/credit.mp4'}
          points={[
            {
              "title": "Global Rating Agency Coverage",
              "details": "Access comprehensive credit ratings, outlooks, and actions from all major agencies including S&P, Moody's, Fitch, and DBRS. Historical rating changes mapped to precise timestamps for accurate backtesting.",
              "cta": "Explore rating sources",
              "url": "/docs"
            },
            {
              "title": "Complete CDS & Spread Intelligence",
              "details": "Monitor CDS spreads across tenors, track bond yield curves, and analyze liquidity metrics across corporate and sovereign issuers. Historical data for relative value and risk analysis.",
              "cta": "View market coverage",
              "url": "/products/stocks"
            },
            {
              "title": "Default Risk & Recovery Analytics",
              "details": "Integrate probability of default metrics, recovery rate estimates, and credit migration matrices directly into your risk models. Credit risk data API powered by market-implied and fundamental data.",
              "cta": "Explore risk metrics",
              "url": "/docs"
            }
          ]}
        />
     <Responses
  heading="Know what the lenders do"
  subheading="Search any institution by name and pull its full credit ratings history in a single call. Built for risk teams, underwriters, and compliance desks who need verified, audit-ready data - not guesswork."
  ticker="AAPL"
  tabs={[
    {
      title: 'Search Organizations',
      description: 'Find the right entity in seconds. Search by name and get back matching organizations with their sector, country, and a unique ID you can use to pull full ratings data.',
      data: [
        {
          id: '689595',
          name: 'Apple Bidco, LLC',
          sectorCode: 'CORP',
          country: 'USA',
          state: 'Texas',
          navigationUrl: null,
        },
        {
          id: '112354',
          name: 'Apple Inc.',
          sectorCode: 'CORP',
          country: 'USA',
          state: 'California',
          navigationUrl: null,
        },
      ],
    },
    {
      title: 'Get Credit Ratings',
      description: 'Pull the complete credit ratings history for any organization - current rating, outlook, review dates, and endorsement status across local and foreign currency, all in one response.',
      data: [
        {
          orgDebtTypeDesc: 'Issuer Credit Rating',
          rating: 'B',
          ratingDate: '06-Jul-2021',
          currentCwOl: 'Stable',
          currentCwOlDate: '06-Jul-2021',
          ratingDebtTypeCode: 'ICR',
          lastReviewDate: '11-Jun-2025',
          coreOrgId: '689595',
          endorsementIndicator: 'EE|UKE',
          coreRatingId: '937228060',
          ratingTypeCode: 'STDLONG',
          ratingTypeCodeDesc: 'Local Currency LT',
          pcrFlag: true,
          id: 1,
        },
        {
          orgDebtTypeDesc: 'Issuer Credit Rating',
          rating: 'B',
          ratingDate: '06-Jul-2021',
          currentCwOl: 'Stable',
          currentCwOlDate: '06-Jul-2021',
          ratingDebtTypeCode: 'ICR',
          lastReviewDate: '11-Jun-2025',
          coreOrgId: '689595',
          endorsementIndicator: 'EE|UKE',
          coreRatingId: '937228062',
          ratingTypeCode: 'FCLONG',
          ratingTypeCodeDesc: 'Foreign Currency LT',
          pcrFlag: true,
          id: 2,
        },
      ],
    },
  ]}
/>
      <DatasetDetails full={true}
        title="Comprehensive Credit Ratings Data Coverage"
        subtitle="Access daily and historical credit ratings data covering over 50,000 issuers and 2 million bonds across investment-grade, high-yield, and municipal securities. Decades of history back to 1987, retrievable nearly instantly. Powers fixed income trading, portfolio risk management, credit research, and regulatory reporting globally."
        cta="Launch Free"
        ctaLink="/login"
        tiles={[
          {
            img: '9.webp',
            title: 'We cover 50,000+ issuers',
            caption: 'You can analyze credit ratings data and credit risk for corporate, sovereign, and municipal entities worldwide.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '7.webp',
            title: 'We track 2M+ bonds',
            caption: 'You can access pricing and analytics for investment-grade, high-yield, and municipal securities.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '2.webp',
            title: 'We provide 500,000+ CDS curves',
            caption: 'You can model default risk and relative value with comprehensive credit default swap data.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '5.webp',
            title: 'We span 85+ credit markets',
            caption: 'You get global coverage of fixed income markets for cross-market analysis.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '10.webp',
            title: 'We offer 35+ years of history',
            caption: 'You can backtest credit strategies and study rating migration cycles across decades.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />
      
      <Outcomes
        headline="Numbers that speak for themselves."
        leftCell={{ stat: '99.95%', title: 'Uptime SLA.', desc: 'Reliable credit ratings data delivery for mission-critical workflows.', showChart: true }}
        rightCells={[
          { stat: '50K+', title: 'Issuers Covered.', desc: 'Corporate, sovereign, and municipal entities worldwide.' },
          { stat: '500K+', title: 'CDS Curves.', desc: 'Full term structure for relative value and risk analysis.', boldDesc: true },
        ]}
      /><ProductFAQ slug="credit" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default CreditPage;
